// the password reset page, behind the "Forgot?" link on the sign-in form.
//
// it does double duty, because a reset is two steps that happen minutes apart:
//   1. 'request' - you type your email and we send you a link
//   2. 'reset'   - you come back via that link and choose a new password
// supabase sends the link back to this same url, so one page covers both.
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { usePageMeta } from '../hooks/usePageMeta';
// reuse the sign-in page's rules rather than writing a second, slightly
// different set that drifts out of step the first time either one changes
import { LIMITS, validateEmail, validatePassword } from './AuthPage';
// and reuse its styles too, so this page looks like part of the same product
import './AuthPage.css';

export default function ForgotPasswordPage() {
  // noIndex: a reset page has no business turning up in search results
  usePageMeta({
    title: 'Reset your password - GPTDisguise',
    description: 'Reset the password on your GPTDisguise account.',
    path: '/forgot',
    noIndex: true
  });

  // which of the two steps we're showing
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // the message under the one input on screen
  const [fieldError, setFieldError] = useState('');
  // the red banner, for anything the server rejects
  const [error, setError] = useState('');
  // the green banner
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // work out whether we're on step 1 or step 2
  useEffect(() => {
    // when someone opens the emailed link, supabase-js reads the token out of
    // the url on its own and fires PASSWORD_RECOVERY once it has a session
    const { data: listener } = supabase.auth.onAuthStateChange(event => {
      if (event === 'PASSWORD_RECOVERY') setStep('reset');
    });

    // that event can fire before this component has finished mounting, so check
    // the url ourselves as well rather than relying on winning the race
    if (window.location.hash.includes('type=recovery')) setStep('reset');

    return () => listener.subscription.unsubscribe();
  }, []);

  // STEP 1 - ask supabase to email a reset link
  const handleRequest = async event => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const message = validateEmail(email);
    setFieldError(message);
    if (message) return;

    setLoading(true);
    const cleanEmail = email.trim();
    const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
      // send them back here, where the effect above will flip us to step 2
      redirectTo: `${window.location.origin}/forgot`
    });
    setLoading(false);

    if (error) {
      setError(error.message);
      return;
    }

    // deliberately the same message whether or not that address has an account.
    // saying "no account found" would turn this form into a way to check which
    // email addresses are registered, which isn't anyone else's business.
    setSuccess(`If an account exists for ${cleanEmail}, a reset link is on its way. Check your inbox and your spam folder - the link is only good for a short while.`);
  };

  // STEP 2 - they followed the link, so set the new password on the recovery session
  const handleReset = async event => {
    event.preventDefault();
    setError('');
    setSuccess('');

    // 'signup' gets the full policy - length, a letter, a number, no stray
    // spaces - because this is a brand new password, not an old one being reused
    const message = validatePassword(password, 'signup');
    setFieldError(message);
    if (message) return;

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      // the usual cause is an expired or already-used link, and supabase's own
      // wording for that is clear enough to pass straight through
      setError(error.message);
      return;
    }

    setSuccess('Your password has been changed. Taking you to the sign-in page...');
    // give them a moment to actually read that before the page changes
    setTimeout(() => navigate('/signin'), 2000);
  };

  const onRequestStep = step === 'request';

  return (
    <div className="auth-page">
      <div className="auth-bg-orb auth-bg-orb--a" />
      <div className="auth-bg-orb auth-bg-orb--b" />

      <button type="button" className="auth-back" onClick={() => navigate('/signin')}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back
      </button>

      <div className="auth-wrap">
        <div className="auth-left">
          <div className="auth-brand">
            <img src="/icon.png" alt="GPTDisguise" className="auth-brand-icon" />
            <span className="auth-brand-name">GPT<em>Disguise</em></span>
          </div>
          <h2 className="auth-left-headline">
            Locked out?<br />
            <span>Let's fix that.</span>
          </h2>
          <p className="auth-left-sub">
            Your plan and your settings stay exactly as they were - only the password changes.
          </p>
        </div>

        <div className="auth-right">
          <div className="auth-card">
            <h1 className="auth-card-heading">
              {onRequestStep ? 'Reset your password' : 'Choose a new password'}
            </h1>
            <p className="auth-card-sub">
              {onRequestStep
                ? "Enter the email address on your account and we'll send you a link."
                : 'Pick something you haven’t used here before.'}
            </p>

            <form className="auth-form" onSubmit={onRequestStep ? handleRequest : handleReset} noValidate>
              {onRequestStep ? (
                <div className="auth-field">
                  <label htmlFor="forgot-email">Email</label>
                  <input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    className={fieldError ? 'has-error' : ''}
                    aria-invalid={fieldError ? 'true' : undefined}
                    aria-describedby={fieldError ? 'forgot-error' : undefined}
                    onChange={e => { setEmail(e.target.value); setFieldError(''); }}
                    onBlur={e => setFieldError(validateEmail(e.target.value))}
                  />
                  {fieldError && <div className="auth-field-error" id="forgot-error" role="alert">{fieldError}</div>}
                </div>
              ) : (
                <div className="auth-field">
                  <label htmlFor="forgot-password">New password</label>
                  <input
                    id="forgot-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder={`At least ${LIMITS.passwordMin} characters`}
                    value={password}
                    className={fieldError ? 'has-error' : ''}
                    aria-invalid={fieldError ? 'true' : undefined}
                    aria-describedby={fieldError ? 'forgot-error' : 'forgot-hint'}
                    onChange={e => { setPassword(e.target.value); setFieldError(''); }}
                    onBlur={e => setFieldError(validatePassword(e.target.value, 'signup'))}
                  />
                  {fieldError
                    ? <div className="auth-field-error" id="forgot-error" role="alert">{fieldError}</div>
                    : <div className="auth-field-hint" id="forgot-hint">At least {LIMITS.passwordMin} characters, including a letter and a number.</div>}
                </div>
              )}

              {error && <div className="auth-message auth-message--error" role="alert">{error}</div>}
              {success && <div className="auth-message auth-message--success" role="status">{success}</div>}

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Please wait...' : onRequestStep ? 'Send reset link' : 'Save new password'}
              </button>
            </form>

            <p className="auth-switch">
              Remembered it? <Link to="/signin" className="auth-switch-btn">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
